import * as React from 'react';
import { CSSProperties, MutableRefObject } from 'react';

function trackFinger(event: TouchEvent | MouseEvent, touchId: React.MutableRefObject<number | undefined>) {
  if (event instanceof TouchEvent && touchId.current !== undefined && event.changedTouches) {
    for (let i = 0; i < event.changedTouches.length; i += 1) {
      const touch = event.changedTouches[i];
      if (touch.identifier === touchId.current) {
        return {
          x: touch.clientX,
          y: touch.clientY
        };
      }
    }
  } else if (event instanceof MouseEvent) {
    return {
      x: event.clientX,
      y: event.clientY
    };
  }
  return false;
}

function getOwnerDocument(element: HTMLElement | null) {
  return element?.ownerDocument ?? document;
}

type Args = {
  onCollapse: () => void;
  collapseBoundaryCoefficient?: number;
  swipeDownBoundaryCoefficient?: number;
};

type Return = {
  // Swipable sheet root
  sheetRef: MutableRefObject<HTMLDivElement | null>;
  // Content area reference
  contentRef: MutableRefObject<HTMLDivElement | null>;
  // Draggable handle reference
  handleRef: MutableRefObject<HTMLDivElement | null>;
  // Translate styles
  sheetStyle: CSSProperties;
  isCollapsed: boolean;
  dragOffset: number;
  progress: number;
};

export function useSwipable({
  onCollapse,
  collapseBoundaryCoefficient = 0.3,
  swipeDownBoundaryCoefficient = 0.2
}: Args): Return {
  const sheetRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const handleRef = React.useRef<HTMLDivElement>(null);
  const touchId = React.useRef<number>();
  const dragStartPosition = React.useRef<number>(0);
  const [dragOffset, setDragOffset] = React.useState(0);
  const maxOffset = React.useRef<number>(0);

  const dragEasingFunction = React.useMemo(
    () => (x: number) => maxOffset.current * Math.tanh(x / maxOffset.current),
    []
  );

  const closeSheet = React.useCallback(() => {
    setDragOffset(Infinity);

    const { current: sheet } = sheetRef;

    if (sheet) {
      sheet.addEventListener('transitionend', onCollapse, { once: true });

      return () => {
        sheet.removeEventListener('transitionend', onCollapse);
      };
    }
  }, [onCollapse]);

  const handleTouchMove = React.useCallback(
    (event: TouchEvent) => {
      const finger = trackFinger(event, touchId);
      if (finger) {
        const calculatedDragOffset = finger.y - dragStartPosition?.current;
        setDragOffset(calculatedDragOffset < 0 ? 0 : dragEasingFunction(calculatedDragOffset));
      }
    },
    [dragEasingFunction]
  );

  const handleTouchEnd = React.useCallback(
    (event: TouchEvent) => {
      const finger = trackFinger(event, touchId);

      if (!finger) {
        return;
      }

      touchId.current = undefined;
      if (finger.y - dragStartPosition?.current > maxOffset.current * collapseBoundaryCoefficient) {
        closeSheet();
      } else {
        setDragOffset(0);
        dragStartPosition.current = 0;
      }

      const doc = getOwnerDocument(handleRef.current);
      doc.removeEventListener('touchmove', handleTouchMove);
      doc.removeEventListener('touchend', handleTouchEnd);
    },
    [closeSheet, collapseBoundaryCoefficient, handleTouchMove]
  );

  const handleTouchStart = React.useCallback(
    (event: TouchEvent) => {
      // todo: remove if nothing breaks
      // Workaround as Safari has partial support for touchAction: 'none'.
      // event.preventDefault();
      const touch = event.changedTouches[0];
      if (touch != null) {
        // A number that uniquely identifies the current finger in the touch session.
        touchId.current = touch.identifier;
      }

      const finger = trackFinger(event, touchId);
      if (finger) {
        dragStartPosition.current = finger.y;
      }

      const doc = getOwnerDocument(handleRef.current);
      doc.addEventListener('touchmove', handleTouchMove);
      doc.addEventListener('touchend', handleTouchEnd);
    },
    [handleTouchEnd, handleTouchMove]
  );

  React.useEffect(() => {
    const { current: handle } = handleRef;
    if (handle !== null) {
      handle.addEventListener('touchstart', handleTouchStart);
    }

    const { current: sheet } = sheetRef;
    const { current: sheetContent } = contentRef;

    if (sheetContent !== null) {
      maxOffset.current = sheetContent.clientHeight * swipeDownBoundaryCoefficient;
    } else if (sheet !== null) {
      maxOffset.current = sheet.clientHeight * swipeDownBoundaryCoefficient;
    }

    return () => {
      if (handle !== null) {
        handle.removeEventListener('touchstart', handleTouchStart);
      }
    };
  }, [handleTouchStart, swipeDownBoundaryCoefficient]);

  const sheetStyle = {
    transform: dragOffset !== 0 && dragOffset !== Infinity ? `translateY(${dragOffset}px)` : undefined,
    '--progress': dragOffset === Infinity ? 1 : dragOffset / (maxOffset.current ?? 0)
  };

  return {
    sheetRef,
    contentRef,
    handleRef,
    sheetStyle,
    isCollapsed: dragOffset === Infinity,
    dragOffset,
    progress: dragOffset === Infinity ? 1 : dragOffset / maxOffset.current ?? 0
  };
}
